/**
 * OneForgeModule (OFM)
 *
 * Displays detailed information about a single Forge, including:
 * - Forge info (name, description, status)
 * - Statistics (application counts)
 * - Related Etabli Domain info
 * - List of applications in this Forge
 */
import { Activity, AppWindow, Boxes, ExternalLink, Folder, GitBranch, LayoutDashboard, Play, Server, Square, Terminal } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import {
  ForgeWithStats,
  ForgePod,
  ForgeLogEntry,
  ForgeLogsResponse,
  ForgeLogsParams,
  ForgeServiceItem,
  ForgeDetail,
  ForgeHealth,
  ForgeArgoCDStatus,
} from '../../types';
import { Badge } from '../Common/badge';
import { ApplicationListModule } from './ApplicationListModule';

// Helper to capitalize first letter
const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper to get color class for log level
const getLogLevelColor = (level?: string): string => {
  if (!level) return 'text-gray-100';
  const upperLevel = level.toUpperCase();
  switch (upperLevel) {
    case 'ERROR':
    case 'CRITICAL':
      return 'text-red-400';
    case 'WARN':
    case 'WARNING':
      return 'text-yellow-400';
    case 'INFO':
      return 'text-blue-400';
    case 'DEBUG':
      return 'text-gray-400';
    default:
      return 'text-gray-100';
  }
};

// Format ISO timestamp to yyyy-mm-dd hh:mm:ss for display
const formatLogTimestamp = (iso: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  } catch {
    return iso;
  }
};

// Helper to format log entry
const formatLogEntry = (entry: ForgeLogEntry): string => {
  const levelStr = entry.level ? `[${entry.level.toUpperCase()}]` : '';
  const ts = formatLogTimestamp(entry.timestamp);
  return `[${ts}] ${levelStr} ${entry.message}`;
};

// ---------------------------------------------------------------------------
// Overview tab — pulls together forge detail / health / ArgoCD / pods.
// ---------------------------------------------------------------------------

const getHealthBadgeVariant = (
  status?: string,
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'healthy':
    case 'synced':
    case 'ready':
      return 'default';
    case 'unknown':
    case 'progressing':
      return 'secondary';
    case 'unhealthy':
    case 'degraded':
    case 'outofsync':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getPodPhaseBadgeVariant = (
  phase?: string,
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!phase) return 'outline';
  switch (phase.toLowerCase()) {
    case 'running':
    case 'succeeded':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'failed':
    case 'unknown':
      return 'destructive';
    default:
      return 'outline';
  }
};

interface OverviewTabContentProps {
  detail: ForgeDetail | null;
  health: ForgeHealth | null;
  argocd: ForgeArgoCDStatus | null;
  pods: ForgePod[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const OverviewTabContent: React.FC<OverviewTabContentProps> = ({
  detail,
  health,
  argocd,
  pods,
  loading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-40 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const allEmpty = !detail && !health && !argocd && pods.length === 0;
  if (allEmpty && error) {
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10 flex items-center justify-between gap-4">
        <p className="text-destructive">{error}</p>
        <button
          onClick={onRetry}
          className="px-3 py-1.5 text-sm rounded-md border border-destructive/30 hover:bg-destructive/10"
        >
          {t('global.retry', 'Retry')}
        </button>
      </div>
    );
  }

  const podsRunning = pods.filter((p) => p.phase?.toLowerCase() === 'running').length;
  const podsTotal = pods.length;
  const argoSyncStatus = (argocd?.sync_status as string | undefined) ?? undefined;
  const argoHealthStatus = (argocd?.health_status as string | undefined) ?? undefined;
  const argoRevision = (argocd?.revision as string | undefined) ?? undefined;
  const argoLastSync = (argocd?.last_sync as string | undefined) ?? undefined;

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 border border-amber-500/50 rounded-lg bg-amber-500/10 flex items-center justify-between gap-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">{error}</p>
          <button
            onClick={onRetry}
            className="px-3 py-1 text-xs rounded-md border border-amber-500/40 hover:bg-amber-500/10"
          >
            {t('global.retry', 'Retry')}
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
            <Server className="h-3.5 w-3.5" />
            {t('forge.overview.status', 'Forge status')}
          </div>
          <div className="mt-2">
            {detail?.status ? (
              <Badge variant={getHealthBadgeVariant(detail.status)}>{detail.status}</Badge>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
            <Activity className="h-3.5 w-3.5" />
            {t('forge.overview.health', 'Health')}
          </div>
          <div className="mt-2 flex items-center gap-2">
            {health?.health_status ? (
              <Badge variant={getHealthBadgeVariant(health.health_status)}>
                {health.health_status}
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
            {typeof health?.uptime_percentage === 'number' && (
              <span className="text-xs text-muted-foreground">
                {health.uptime_percentage.toFixed(1)}%
              </span>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
            <GitBranch className="h-3.5 w-3.5" />
            {t('forge.overview.argocd', 'ArgoCD')}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {argoSyncStatus && (
              <Badge variant={getHealthBadgeVariant(argoSyncStatus)}>{argoSyncStatus}</Badge>
            )}
            {argoHealthStatus && (
              <Badge variant={getHealthBadgeVariant(argoHealthStatus)}>{argoHealthStatus}</Badge>
            )}
            {!argoSyncStatus && !argoHealthStatus && (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
            <Boxes className="h-3.5 w-3.5" />
            {t('forge.overview.pods', 'Pods')}
          </div>
          <div className="mt-2 text-foreground">
            <span className="text-2xl font-semibold">{podsRunning}</span>
            <span className="text-muted-foreground text-sm"> / {podsTotal} {t('forge.overview.running', 'running')}</span>
          </div>
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border rounded-lg bg-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" />
            {t('forge.overview.details', 'Details')}
          </h3>
          {detail ? (
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
              <span className="text-muted-foreground">{t('forge.overview.name', 'Name')}</span>
              <span className="text-foreground">{detail.display_name || detail.name}</span>
              <span className="text-muted-foreground">{t('forge.overview.systemApps', 'System apps')}</span>
              <span className="text-foreground">{detail.system_apps.length}</span>
              <span className="text-muted-foreground">{t('forge.overview.customApps', 'Custom apps')}</span>
              <span className="text-foreground">{detail.custom_apps.length}</span>
              {detail.forge_infra_repo && (
                <>
                  <span className="text-muted-foreground">{t('forge.overview.infraRepo', 'Infra repo')}</span>
                  <a
                    href={detail.forge_infra_repo}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline truncate inline-flex items-center gap-1"
                  >
                    {detail.forge_infra_repo}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </>
              )}
              {detail.created_at && (
                <>
                  <span className="text-muted-foreground">{t('forge.overview.created', 'Created')}</span>
                  <span className="text-foreground">
                    {new Date(detail.created_at).toLocaleString()}
                  </span>
                </>
              )}
              {detail.updated_at && (
                <>
                  <span className="text-muted-foreground">{t('forge.overview.updated', 'Updated')}</span>
                  <span className="text-foreground">
                    {new Date(detail.updated_at).toLocaleString()}
                  </span>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('forge.overview.noDetail', 'Forge details unavailable')}
            </p>
          )}

          {detail && Object.keys(detail.forge_urls || {}).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                {t('forge.overview.forgeUrls', 'Forge URLs')}
              </p>
              <ul className="space-y-1 text-xs">
                {Object.entries(detail.forge_urls).map(([key, url]) => (
                  <li key={key} className="flex items-center gap-2">
                    <span className="text-muted-foreground min-w-[80px]">{key}</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline truncate inline-flex items-center gap-1"
                    >
                      {url}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border border-border rounded-lg bg-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            {t('forge.overview.healthDetail', 'Health detail')}
          </h3>
          {health ? (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5">
                <span className="text-muted-foreground">{t('forge.overview.errors', 'Errors')}</span>
                <span className="text-foreground">{health.error_count}</span>
                <span className="text-muted-foreground">{t('forge.overview.lastCheck', 'Last check')}</span>
                <span className="text-foreground">
                  {health.last_health_check
                    ? new Date(health.last_health_check).toLocaleString()
                    : '-'}
                </span>
                <span className="text-muted-foreground">{t('forge.overview.accessibleUrls', 'Accessible URLs')}</span>
                <span className="text-foreground">{health.accessible_urls.length}</span>
                <span className="text-muted-foreground">{t('forge.overview.failedUrls', 'Failed URLs')}</span>
                <span className="text-foreground">{health.failed_urls.length}</span>
              </div>
              {Object.keys(health.system_apps_health || {}).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2 mb-1">
                    {t('forge.overview.systemAppsHealth', 'System apps health')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(health.system_apps_health).map(([app, st]) => (
                      <Badge key={app} variant={getHealthBadgeVariant(st)} className="font-normal">
                        {app}: {st}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(health.custom_apps_health || {}).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-2 mb-1">
                    {t('forge.overview.customAppsHealth', 'Custom apps health')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(health.custom_apps_health).map(([app, st]) => (
                      <Badge key={app} variant={getHealthBadgeVariant(st)} className="font-normal">
                        {app}: {st}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('forge.overview.noHealth', 'Health data unavailable')}
            </p>
          )}
        </div>

        <div className="border border-border rounded-lg bg-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-primary" />
            {t('forge.overview.argocdDetail', 'ArgoCD detail')}
          </h3>
          {argocd ? (
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
              <span className="text-muted-foreground">{t('forge.overview.sync', 'Sync')}</span>
              <span className="text-foreground">{argoSyncStatus || '-'}</span>
              <span className="text-muted-foreground">{t('forge.overview.health', 'Health')}</span>
              <span className="text-foreground">{argoHealthStatus || '-'}</span>
              <span className="text-muted-foreground">{t('forge.overview.revision', 'Revision')}</span>
              <span className="text-foreground font-mono text-xs break-all">
                {argoRevision || '-'}
              </span>
              <span className="text-muted-foreground">{t('forge.overview.lastSync', 'Last sync')}</span>
              <span className="text-foreground">
                {argoLastSync ? new Date(argoLastSync).toLocaleString() : '-'}
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('forge.overview.noArgo', 'ArgoCD status unavailable')}
            </p>
          )}
        </div>

        <div className="border border-border rounded-lg bg-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Boxes className="h-4 w-4 text-primary" />
            {t('forge.overview.podsDetail', 'Pods')}
          </h3>
          {pods.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('forge.overview.noPods', 'No pods')}
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {pods.map((pod) => (
                <li key={pod.name} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-foreground truncate" title={pod.name}>{pod.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('forge.overview.restarts', 'restarts')}: {pod.restarts}
                      {typeof pod.ready === 'boolean' &&
                        ` · ${t('forge.overview.ready', 'ready')}: ${pod.ready ? '✓' : '✗'}`}
                    </p>
                  </div>
                  <Badge variant={getPodPhaseBadgeVariant(pod.phase)}>{pod.phase}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Services tab — one card per data service running on the forge.
// ---------------------------------------------------------------------------

const getServiceStatusBadgeVariant = (
  status?: string,
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'running':
    case 'ready':
    case 'deployed':
    case 'active':
      return 'default';
    case 'creating':
    case 'pending':
    case 'updating':
      return 'secondary';
    case 'error':
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatServiceTimestamp = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

interface ServicesTabContentProps {
  services: ForgeServiceItem[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const ServicesTabContent: React.FC<ServicesTabContentProps> = ({
  services,
  loading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10 flex items-center justify-between gap-4">
        <p className="text-destructive">{error}</p>
        <button
          onClick={onRetry}
          className="px-3 py-1.5 text-sm rounded-md border border-destructive/30 hover:bg-destructive/10"
        >
          {t('global.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="p-8 text-center border border-border rounded-lg bg-muted/30">
        <Boxes className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">
          {t('forge.services.empty', 'No services deployed in this forge')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {services.map((svc) => {
        const endpointEntries = Object.entries(svc.endpoints ?? {});
        return (
          <div
            key={svc.service_id}
            className="border border-border rounded-lg bg-card p-4 flex flex-col gap-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-primary shrink-0" />
                  <h3 className="text-base font-semibold text-foreground truncate" title={svc.display_name}>
                    {svc.display_name || svc.service_name}
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate" title={svc.service_name}>
                  {svc.service_name}
                </p>
              </div>
              <Badge variant={getServiceStatusBadgeVariant(svc.status)}>{svc.status}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
              <span className="text-muted-foreground">{t('forge.services.type', 'Type')}</span>
              <span className="text-foreground font-medium truncate" title={svc.service_type}>
                {svc.service_type}
              </span>
              <span className="text-muted-foreground">{t('forge.services.template', 'Template')}</span>
              <span className="text-foreground truncate" title={svc.template_name}>
                {svc.template_name}
              </span>
              <span className="text-muted-foreground">{t('forge.services.created', 'Created')}</span>
              <span className="text-foreground">{formatServiceTimestamp(svc.created_at)}</span>
              <span className="text-muted-foreground">{t('forge.services.updated', 'Updated')}</span>
              <span className="text-foreground">{formatServiceTimestamp(svc.updated_at)}</span>
            </div>

            {endpointEntries.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  {t('forge.services.endpoints', 'Endpoints')}
                </p>
                <ul className="space-y-1 text-xs font-mono">
                  {endpointEntries.map(([key, value]) => (
                    <li key={key} className="flex flex-col">
                      <span className="text-muted-foreground">{key}</span>
                      <span className="text-foreground break-all">
                        {typeof value === 'string' ? value : JSON.stringify(value)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-[10px] text-muted-foreground/70 mt-auto pt-1 break-all">
              ID: {svc.service_id}
            </div>
          </div>
        );
      })}
    </div>
  );
};

type ForgeTabId = 'overview' | 'applications' | 'services' | 'logs';

interface OneForgeModuleProps {
  forgeId: string;
  domainId?: string | null;  // Related Etabli domain ID
  domainName?: string | null;  // Related Etabli domain name
  showHeader?: boolean;  // Show header with icon and title (set to false when used in detail page)
  hideDomainLink?: boolean;  // Hide Domain link (when shown in Domain's tabs)
  showOverview?: boolean;  // Show overview tab (status / health / argocd / pods)
  showApplicationsList?: boolean;  // Show embedded applications list
  showServices?: boolean;  // Show services tab
  showLogs?: boolean;  // Show logs tab
  onApplicationClick?: (appId: string) => void;
  className?: string;
}

export const OneForgeModule: React.FC<OneForgeModuleProps> = ({
  forgeId,
  domainId,
  domainName,
  showHeader = true,
  hideDomainLink = false,
  showOverview = false,
  showApplicationsList = true,
  showServices = false,
  showLogs = false,
  onApplicationClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const [forge, setForge] = useState<ForgeWithStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const defaultTab: ForgeTabId = showOverview
    ? 'overview'
    : showApplicationsList
      ? 'applications'
      : showServices
        ? 'services'
        : 'logs';
  const [activeTab, setActiveTab] = useState<ForgeTabId>(defaultTab);

  // Overview state (lazy fetched when the tab is opened)
  const [overviewDetail, setOverviewDetail] = useState<ForgeDetail | null>(null);
  const [overviewHealth, setOverviewHealth] = useState<ForgeHealth | null>(null);
  const [overviewArgoCD, setOverviewArgoCD] = useState<ForgeArgoCDStatus | null>(null);
  const [overviewPods, setOverviewPods] = useState<ForgePod[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  // Services state
  const [services, setServices] = useState<ForgeServiceItem[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);

  // Logs state
  const [pods, setPods] = useState<ForgePod[]>([]);
  const [selectedPod, setSelectedPod] = useState<string>('');
  const [logs, setLogs] = useState<ForgeLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [tailLines, setTailLines] = useState<number>(200);
  const [logLevel, setLogLevel] = useState<string>('');
  const [fromTime, setFromTime] = useState<string>('');
  const [toTime, setToTime] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filteredCount, setFilteredCount] = useState<number>(0);

  useEffect(() => {
    const fetchForge = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.getForgeWithStats(forgeId);
        setForge(data);
      } catch (err: any) {
        setError(err?.message || t('forge.detail.errorLoading', 'Failed to load Forge details'));
        console.error('Error fetching Forge:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForge();
  }, [forgeId, t]);

  // Fetch pods when logs tab is active
  useEffect(() => {
    if (showLogs && activeTab === 'logs' && forge) {
      fetchPods();
    }
  }, [showLogs, activeTab, forge]);

  // Fetch services when services tab is active
  useEffect(() => {
    if (showServices && activeTab === 'services' && forge) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showServices, activeTab, forge]);

  // Fetch overview data lazily when the overview tab is activated
  useEffect(() => {
    if (showOverview && activeTab === 'overview' && forge) {
      fetchOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOverview, activeTab, forge]);

  // Fetch logs when pod or any filter value changes
  useEffect(() => {
    if (showLogs && activeTab === 'logs' && forge && selectedPod) {
      fetchLogs();
    }
  }, [showLogs, activeTab, forge, selectedPod, tailLines, logLevel, fromTime, toTime]);

  const fetchPods = async () => {
    if (!forge) return;

    try {
      const podsData = await forgeService.getForgePods(forge.client_id, forge.forge_id);
      setPods(podsData);
      if (podsData.length > 0 && !selectedPod) {
        setSelectedPod(podsData[0].name);
      }
    } catch (err: any) {
      console.error('Error fetching pods:', err);
      setLogsError(err?.message || 'Failed to load pods');
    }
  };

  const fetchOverview = async () => {
    if (!forge) return;
    setOverviewLoading(true);
    setOverviewError(null);
    const [detailRes, healthRes, argoRes, podsRes] = await Promise.allSettled([
      forgeService.getForgeDetailView(forge.client_id, forge.forge_id),
      forgeService.getForgeHealth(forge.client_id, forge.forge_id),
      forgeService.getForgeArgoCDStatus(forge.client_id, forge.forge_id),
      forgeService.getForgePods(forge.client_id, forge.forge_id),
    ]);
    setOverviewDetail(detailRes.status === 'fulfilled' ? detailRes.value : null);
    setOverviewHealth(healthRes.status === 'fulfilled' ? healthRes.value : null);
    setOverviewArgoCD(argoRes.status === 'fulfilled' ? argoRes.value : null);
    setOverviewPods(podsRes.status === 'fulfilled' ? podsRes.value : []);

    const failures = [detailRes, healthRes, argoRes, podsRes].filter(
      (r) => r.status === 'rejected',
    );
    if (failures.length === 4) {
      setOverviewError(t('forge.overview.errorAll', 'Failed to load overview data'));
    } else if (failures.length > 0) {
      setOverviewError(
        t('forge.overview.errorPartial', 'Some overview data failed to load'),
      );
    }
    setOverviewLoading(false);
  };

  const fetchServices = async () => {
    if (!forge) return;
    setServicesLoading(true);
    setServicesError(null);
    try {
      const data = await forgeService.listForgeServices(forge.client_id, forge.forge_id);
      setServices(data);
    } catch (err: unknown) {
      console.error('Error fetching services:', err);
      const msg = err instanceof Error ? err.message : null;
      setServicesError(msg || t('forge.services.errorLoading', 'Failed to load services'));
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchLogs = async () => {
    if (!forge || !selectedPod) return;

    setLogsLoading(true);
    setLogsError(null);
    try {
      const params: ForgeLogsParams = {
        pod: selectedPod,
        tail_lines: tailLines,
      };

      if (logLevel && logLevel !== 'all') {
        params.log_level = logLevel;
      }
      if (fromTime.trim()) {
        params.from_time = fromTime.trim();
      }
      if (toTime.trim()) {
        params.to_time = toTime.trim();
      }

      const response: ForgeLogsResponse = await forgeService.getForgeLogs(forge.client_id, forge.forge_id, params);
      setLogs(response.logs);
      setTotalCount(response.total_count);
      setFilteredCount(response.filtered_count);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      setLogsError(err?.message || 'Failed to load logs');
      setLogs([]);
      setTotalCount(0);
      setFilteredCount(0);
    } finally {
      setLogsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-muted rounded w-2/3 mb-6"></div>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 border border-destructive/50 rounded-lg bg-destructive/10 ${className}`}>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!forge) {
    return (
      <div className={`p-6 text-center border border-border rounded-lg bg-muted/30 ${className}`}>
        <Server className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">{t('forge.detail.notFound', 'Forge not found')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header - only shown when showHeader is true */}
      {showHeader && (
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Server className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-foreground">{capitalizeFirst(forge.display_name || forge.name)}</h2>
              {forge.status && (
                <Badge variant={forge.status === 'deployed' ? 'default' : 'secondary'}>
                  {forge.status}
                </Badge>
              )}
            </div>
            {forge.display_name && forge.display_name !== forge.name && (
              <p className="text-muted-foreground mt-2">{forge.name}</p>
            )}
          </div>
        </div>
      )}

      {/* Related Domain */}
      {!hideDomainLink && domainId && (
        <div className="p-4 border border-border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Folder className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('forge.detail.linkedDomain', 'Linked Etabli Domain')}
                </p>
                <p className="text-foreground font-medium mt-0.5">
                  {domainName || t('global.domain', 'Domain')}
                </p>
              </div>
            </div>
            <Link
              to={`/domains/${domainId}`}
              className="flex items-center gap-1.5 text-primary hover:underline text-sm"
            >
              <Folder className="h-4 w-4" />
              {t('forge.detail.viewDomain', 'View Domain')}
            </Link>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 border border-border rounded-lg bg-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <AppWindow className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{forge.applications_count}</p>
              <p className="text-sm text-muted-foreground">
                {t('forge.detail.totalApplications', 'Total Applications')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 border border-border rounded-lg bg-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Play className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{forge.running_applications}</p>
              <p className="text-sm text-muted-foreground">
                {t('forge.detail.runningApplications', 'Running')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 border border-border rounded-lg bg-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <Square className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{forge.stopped_applications}</p>
              <p className="text-sm text-muted-foreground">
                {t('forge.detail.stoppedApplications', 'Stopped')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs for Overview, Applications, Services and Logs */}
      {(showOverview || showApplicationsList || showServices || showLogs) && (
        <div>
          <div className="flex border-b border-border mb-4">
            {showOverview && (
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  activeTab === 'overview'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutDashboard className="h-4 w-4" />
                {t('forge.detail.overview', 'Overview')}
              </button>
            )}
            {showApplicationsList && (
              <button
                onClick={() => setActiveTab('applications')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  activeTab === 'applications'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <AppWindow className="h-4 w-4" />
                {t('forge.detail.applications', 'Applications')}
              </button>
            )}
            {showServices && (
              <button
                onClick={() => setActiveTab('services')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  activeTab === 'services'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Boxes className="h-4 w-4" />
                {t('forge.detail.services', 'Services')}
              </button>
            )}
            {showLogs && (
              <button
                onClick={() => setActiveTab('logs')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  activeTab === 'logs'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Terminal className="h-4 w-4" />
                {t('forge.detail.logs', 'Logs')}
              </button>
            )}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && showOverview && (
            <OverviewTabContent
              detail={overviewDetail}
              health={overviewHealth}
              argocd={overviewArgoCD}
              pods={overviewPods}
              loading={overviewLoading}
              error={overviewError}
              onRetry={fetchOverview}
            />
          )}

          {/* Applications List */}
          {activeTab === 'applications' && showApplicationsList && (
            <ApplicationListModule
              forgeId={forgeId}
              onApplicationClick={onApplicationClick}
            />
          )}

          {/* Services Tab */}
          {activeTab === 'services' && showServices && (
            <ServicesTabContent
              services={services}
              loading={servicesLoading}
              error={servicesError}
              onRetry={fetchServices}
            />
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && showLogs && (
            <div className="space-y-4">
              {/* Filters and Controls */}
              <div className="flex flex-wrap gap-4 items-end">
                {/* Pod Selection */}
                <div className="flex-1 min-w-48">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('forge.logs.selectPod', 'Select Pod')}
                  </label>
                  <select
                    value={selectedPod}
                    onChange={(e) => setSelectedPod(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">{t('forge.logs.selectPodPlaceholder', 'Select a pod...')}</option>
                    {pods.map((pod) => (
                      <option key={pod.name} value={pod.name}>
                        {pod.name} ({pod.phase})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Log Level Filter */}
                <div className="w-48">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('forge.logs.logLevel', 'Log Level')}
                  </label>
                  <select
                    value={logLevel}
                    onChange={(e) => setLogLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">{t('forge.logs.allLevels', 'All')}</option>
                    <option value="DEBUG">DEBUG</option>
                    <option value="INFO">INFO</option>
                    <option value="WARN">WARN</option>
                    <option value="WARNING">WARNING</option>
                    <option value="ERROR">ERROR</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                </div>

                {/* Time range: From */}
                <div className="w-52">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('forge.logs.fromTime', 'From')}
                  </label>
                  <input
                    type="datetime-local"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:dark]"
                  />
                </div>

                {/* Time range: To */}
                <div className="w-52">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('forge.logs.toTime', 'To')}
                  </label>
                  <input
                    type="datetime-local"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:dark]"
                  />
                </div>

                {/* Tail Lines */}
                <div className="w-32">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('forge.logs.tailLines', 'Lines')}
                  </label>
                  <select
                    value={tailLines}
                    onChange={(e) => setTailLines(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                    <option value={5000}>5000</option>
                  </select>
                </div>
              </div>

              {/* Logs Display */}
              <div className="border border-border rounded-lg bg-gray-900 text-gray-100 overflow-hidden">
                <div className="max-h-[600px] overflow-auto">
                  {logsLoading ? (
                    <div className="text-center py-8 text-gray-400">
                      {t('forge.logs.loading', 'Loading logs...')}
                    </div>
                  ) : logsError ? (
                    <div className="text-center py-8 text-red-400 px-4">
                      {logsError}
                    </div>
                  ) : !selectedPod ? (
                    <div className="text-center py-8 text-gray-400">
                      {t('forge.logs.selectPodMessage', 'Please select a pod to view logs')}
                    </div>
                  ) : !logs || logs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      {t('forge.logs.noLogs', 'No logs available')}
                    </div>
                  ) : (
                    <div className="p-4 font-mono text-xs">
                      {logs.map((entry, index) => (
                        <div key={index} className={`whitespace-pre-wrap break-words ${getLogLevelColor(entry.level)}`}>
                          {formatLogEntry(entry)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Logs Info */}
              {logs && logs.length > 0 && !logsLoading && (
                <div className="text-sm text-muted-foreground text-center">
                  {filteredCount !== totalCount ? (
                    <>
                      {t('forge.logs.showing', 'Showing')} {filteredCount} {t('forge.logs.of', 'of')} {totalCount} {t('forge.logs.lines', 'lines')}
                    </>
                  ) : (
                    <>
                      {t('forge.logs.showing', 'Showing')} {filteredCount} {t('forge.logs.lines', 'lines')}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OneForgeModule;
